import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Icon, WhatsAppIcon } from "@/components/Icon";
import { NAV, WA_DEFAULT } from "@/data/site";
import { SERVICES } from "@/data/services";
import { INDUSTRY_PAGES } from "@/data/industries";

const MenuPanel = ({ type, onNavigate }) => {
  const isS = type === "services";
  const items = isS ? SERVICES : INDUSTRY_PAGES;
  return (
    <div className="wrap grid gap-10 py-10 lg:grid-cols-[1fr_3fr]">
      <div>
        <p className="eyebrow">{isS ? "12 core services" : "Sector expertise"}</p>
        <p className="mt-4 font-display text-2xl font-bold leading-tight">{isS ? "Complete digital solutions for industrial businesses." : "Solutions shaped around how each sector works."}</p>
        <Link to={isS ? "/services" : "/industries"} onClick={onNavigate} data-testid={`menu-${type}-all-link`} className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-rust-ink hover:text-charcoal">
          {isS ? "View full service catalogue" : "View all 24 industries"} <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
      <ul className="grid gap-x-8 gap-y-1 sm:grid-cols-2 xl:grid-cols-3">
        {items.map((it) => (
          <li key={it.slug}>
            <Link
              to={isS ? `/services/${it.slug}` : `/industries/${it.slug}`}
              onClick={onNavigate}
              data-testid={`menu-${type}-${it.slug}`}
              className="group flex items-center gap-3 rounded-md px-3 py-3 text-[15px] text-graphite transition-colors hover:bg-stone hover:text-charcoal"
            >
              {isS && <Icon name={it.icon} className="h-4 w-4 text-rust-deep" />}
              <span>{isS ? it.title : it.name}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

const MobileMenu = ({ onClose }) => {
  const [open, setOpen] = useState(null);
  return (
    <div id="mobile-menu" className="fixed inset-0 top-[72px] z-40 overflow-y-auto bg-ivory pb-32 lg:hidden" data-testid="mobile-menu">
      <nav aria-label="Mobile" className="wrap py-6">
        {NAV.map((n) => (
          <div key={n.to} className="border-b border-stone">
            {n.menu ? (
              <>
                <button
                  type="button"
                  aria-expanded={open === n.menu}
                  onClick={() => setOpen(open === n.menu ? null : n.menu)}
                  data-testid={`mobile-nav-${n.menu}-toggle`}
                  className="flex w-full items-center justify-between py-4 font-display text-2xl font-bold"
                >
                  {n.label} <ChevronDown className={`h-5 w-5 transition-transform ${open === n.menu ? "rotate-180" : ""}`} />
                </button>
                {open === n.menu && (
                  <ul className="pb-4">
                    <li><Link to={n.to} onClick={onClose} className="block py-2 text-[15px] font-semibold text-rust-ink">All {n.label.toLowerCase()}</Link></li>
                    {(n.menu === "services" ? SERVICES : INDUSTRY_PAGES).map((it) => (
                      <li key={it.slug}>
                        <Link to={`/${n.menu}/${it.slug}`} onClick={onClose} data-testid={`mobile-menu-${it.slug}`} className="block py-2 text-[15px] text-graphite">
                          {it.title || it.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : (
              <Link to={n.to} onClick={onClose} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`} className="block py-4 font-display text-2xl font-bold">
                {n.label}
              </Link>
            )}
          </div>
        ))}
        <div className="mt-8 grid gap-3">
          <Link to="/contact" onClick={onClose} className="btn-primary" data-testid="mobile-menu-quote-btn">Discuss Your Project</Link>
          <a href={WA_DEFAULT} target="_blank" rel="noopener noreferrer" className="btn-ghost" data-testid="mobile-menu-whatsapp-btn">
            <WhatsAppIcon className="h-4 w-4" /> Chat on WhatsApp
          </a>
        </div>
      </nav>
    </div>
  );
};

export const Header = () => {
  const [menu, setMenu] = useState(null);
  const [mobile, setMobile] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const ref = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => { setMenu(null); setMobile(false); }, [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    const onKey = (e) => { if (e.key === "Escape") { setMenu(null); setMobile(false); } };
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setMenu(null); };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onClick); };
  }, []);
  useEffect(() => {
    document.body.style.overflow = mobile ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobile]);

  return (
    <header ref={ref} onMouseLeave={() => setMenu(null)} className={`sticky top-0 z-50 border-b transition-colors duration-300 ${scrolled || menu || mobile ? "border-stone bg-ivory/90 backdrop-blur-xl" : "border-transparent bg-ivory/60 backdrop-blur-md"}`} data-testid="site-header">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:rounded focus:bg-charcoal focus:px-4 focus:py-2 focus:text-ivory">Skip to content</a>
      <div className="wrap flex h-[72px] items-center justify-between gap-6">
        <Logo className="h-9 sm:h-10" />
        <nav aria-label="Main" className="hidden items-center gap-1 lg:flex">
          {NAV.map((n) =>
            n.menu ? (
              <div key={n.to} className="relative" onMouseEnter={() => setMenu(n.menu)}>
                <button
                  type="button"
                  aria-expanded={menu === n.menu}
                  aria-controls={`menu-${n.menu}`}
                  onClick={() => setMenu(menu === n.menu ? null : n.menu)}
                  data-testid={`nav-${n.menu}-toggle`}
                  className={`flex items-center gap-1 rounded-full px-4 py-2 text-[15px] font-medium transition-colors hover:text-rust-ink ${pathname.startsWith(n.to) ? "text-rust-ink" : "text-charcoal"}`}
                >
                  {n.label} <ChevronDown className={`h-4 w-4 transition-transform ${menu === n.menu ? "rotate-180" : ""}`} />
                </button>
              </div>
            ) : (
              <NavLink
                key={n.to}
                to={n.to}
                end
                onMouseEnter={() => setMenu(null)}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) => `rounded-full px-4 py-2 text-[15px] font-medium transition-colors hover:text-rust-ink ${isActive ? "text-rust-ink" : "text-charcoal"}`}
              >
                {n.label}
              </NavLink>
            )
          )}
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/contact" className="btn-primary hidden !min-h-[44px] sm:inline-flex" data-testid="header-cta-btn">Discuss Your Project</Link>
          <button
            type="button"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-charcoal/20 lg:hidden"
            aria-label={mobile ? "Close menu" : "Open menu"}
            aria-expanded={mobile}
            aria-controls="mobile-menu"
            onClick={() => setMobile(!mobile)}
            data-testid="mobile-menu-toggle"
          >
            {mobile ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {menu && (
        <div id={`menu-${menu}`} className="absolute inset-x-0 top-full hidden border-b border-stone bg-ivory shadow-[0_30px_60px_-30px_rgba(39,41,35,.35)] lg:block" data-testid={`menu-${menu}-panel`}>
          <MenuPanel type={menu} onNavigate={() => setMenu(null)} />
        </div>
      )}
      {mobile && createPortal(<MobileMenu onClose={() => setMobile(false)} />, document.body)}
    </header>
  );
};
